import GlobalUploadProgress from '@/components/global-upload-progress';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import i18n from '@/locales/config';
import { LanguageAbbreviation } from '@/constants/common';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App, ConfigProvider, ConfigProviderProps, theme } from 'antd';
import pt_BR from 'antd/lib/locale/pt_BR';
import deDE from 'antd/locale/de_DE';
import enUS from 'antd/locale/en_US';
import vi_VN from 'antd/locale/vi_VN';
import zhCN from 'antd/locale/zh_CN';
import zh_HK from 'antd/locale/zh_HK';
import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import React, { ReactNode, useEffect, useState } from 'react';
import { ThemeProvider, useTheme } from './components/theme-provider';
import { SidebarProvider } from './components/ui/sidebar';
import { TooltipProvider } from './components/ui/tooltip';
import storage from './utils/authorization-util';
import {
  isInIframe,
  setupGlobalMessageHandler,
} from './utils/iframe-message-handler';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const AntLanguageMap = {
  en: enUS,
  zh: zhCN,
  'zh-TRADITIONAL': zh_HK,
  vi: vi_VN,
  'pt-BR': pt_BR,
  de: deDE,
};

if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React, {
    trackAllPureComponents: true,
    trackExtraHooks: [],
    logOnDifferentValues: true,
  });
}

const queryClient = new QueryClient();

type Locale = ConfigProviderProps['locale'];

function Root({ children }: React.PropsWithChildren) {
  const { theme: themeragflow } = useTheme();
  const getLocale = (lng: string) =>
    AntLanguageMap[lng as keyof typeof AntLanguageMap] ?? enUS;

  const [locale, setLocal] = useState<Locale>(getLocale(storage.getLanguage()));

  i18n.on('languageChanged', function (lng: string) {
    storage.setLanguage(lng);
    setLocal(getLocale(lng));
  });

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: 'Inter',
          },
          algorithm:
            themeragflow === 'dark'
              ? theme.darkAlgorithm
              : theme.defaultAlgorithm,
        }}
        locale={locale}
      >
        <SidebarProvider>
          <App>{children}</App>
        </SidebarProvider>
        <GlobalUploadProgress />
        <Sonner position={'top-right'} expand richColors closeButton></Sonner>
        <Toaster />
      </ConfigProvider>
      <ReactQueryDevtools buttonPosition={'top-left'} />
    </>
  );
}

const RootProvider = ({ children }: React.PropsWithChildren) => {
  useEffect(() => {
    // 在iframe环境中，语言由PostMessage控制，避免冲突
    if (isInIframe()) {
      console.log(
        '🔍 Iframe mode detected, skipping localStorage language initialization',
      );
      return;
    }

    // Because the language is saved in the backend, a token is required to obtain the api. However, the login page cannot obtain the language through the getUserInfo api, so the language needs to be saved in localstorage.
    const lng = storage.getLanguage();
    if (lng) {
      i18n.changeLanguage(lng);
    } else {
      // 优先使用 window.__SYSTEM_DEFAULT_LANGUAGE__（从 index.html 中的 script 标签设置）
      const windowLang = (window as any).__SYSTEM_DEFAULT_LANGUAGE__;
      if (
        windowLang &&
        Object.values(LanguageAbbreviation).includes(
          windowLang as LanguageAbbreviation,
        )
      ) {
        // 直接使用 window 中的默认语言
        i18n.changeLanguage(windowLang);
      } else {
        // 如果没有 window 中的语言，从 conf.json 读取系统默认语言
        axios
          .get('/conf.json')
          .then((response) => {
            const systemDefaultLanguage =
              response.data?.systemDefaultLanguage || 'en';
            if (
              systemDefaultLanguage &&
              Object.values(LanguageAbbreviation).includes(
                systemDefaultLanguage as LanguageAbbreviation,
              )
            ) {
              // 设置到 window 对象，供 config.ts 使用
              (window as any).__SYSTEM_DEFAULT_LANGUAGE__ = systemDefaultLanguage;
              // 设置语言并保存到 localStorage
              i18n.changeLanguage(systemDefaultLanguage);
            }
          })
          .catch((error) => {
            console.warn('Failed to load conf.json:', error);
          });
      }
    }
  }, []);

  // 设置全局消息处理器
  useEffect(() => {
    const cleanup = setupGlobalMessageHandler();
    return cleanup;
  }, []);

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light">
          <Root>{children}</Root>
        </ThemeProvider>
      </QueryClientProvider>
    </TooltipProvider>
  );
};
export function rootContainer(container: ReactNode) {
  return <RootProvider>{container}</RootProvider>;
}
